<template>
    <div class="min-h-screen bg-white">
        <section>
            <section class="relative overflow-hidden">
                <div class="
                    bg-blog-beige bg-center bg-no-repeat bg-cover flex justify-center items-center py-16 md:py-24 border-t border-b border-blog-gold" 
                    style="min-height: 500px"
                >
                    <div 
                        class="flex items-center justify-center w-96 h-96 absolute circle-rotating-logo"
                    >
                        <div class="absolute w-full h-full animate-spin-slow">
                            <svg viewBox="0 0 100 100" class="w-full h-full">
                            <defs>
                                <path id="circlePath" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
                            </defs>
                            <text class="f-font-times-new-roman-spaced fill-blog-red text-[1rem] uppercase">
                                <textPath href="#circlePath">
                                Teacher Maestra Victoria  Teacher Maestra Victoria  Teacher Maestra Victoria 
                                Teacher Maestra Victoria  Teacher Maestra Victoria  Teacher Maestra Victoria
                                </textPath>
                            </text>
                            </svg>
                        </div>
                    </div>

                    <div class="container mx-auto px-4 text-center">
                        <div class="animate-slide-down-1">
                            <h1 class="f-font-times-new-roman text-5xl md:text-7xl text-blog-old-red mb-2">
                                {{ translate('online english and spanish with', 'ucfirst') }}
                            </h1>

                            <div class="
                                inline-block backdrop-blur-sm px-4 py-2 mb-8 bg-[url('homepage/home_page_small_bg.webp')] 
                                bg-center bg-no-repeat bg-cover
                            ">
                                <h2 class="f-font-times-new-roman text-4xl md:text-6xl text-blog-old-red">
                                    {{ translate('grace, freedom, and purpose') }}
                                </h2>
                            </div>
                        </div>

                        <div class="max-w-2xl mx-auto text-blog-old-red font-light leading-relaxed f-font-helvetica animate-slide-down-3">
                            <p class="text-lg md:text-xl">
                                {{ translate('no pressure, no one-size-fits-all formulas', 'ucfirst') }}.
                            </p>
                            <p class="text-base md:text-lg">
                                {{ translate('a learning experience that', 'ucfirst') }} 
                                <span class="font-bold">{{ translate('respects your pace') }}</span>,
                                <span class="underline underline-offset-4 decoration-1 mr-1 md:mr-0">{{ translate('values your interests') }}</span>
                                <br class="hidden md:block">
                                <span class="underline underline-offset-4 decoration-1">
                                    {{ translate('and turns the process into something natural') }}.
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div class="text-center my-4 f-font-times-new-roman text-blog-old-red text-2xl mb-10">
                <span 
                    v-for="(char, index) in getTitleMessageArray()" 
                    :key="index"
                    class="letter-animate opacity-0"
                    :style="{ animationDelay: `${index * 0.05}s` }"
                    >
                    {{ char === ' ' ? '\u00A0' : char }}
                </span>
            </div>


            <main class="px-8 md:px-20 mx-auto py-12">
                <div 
                    class="flex justify-between items-end mb-10" 
                    v-if="loadedData.loadingCourses || (!loadedData.loadingCourses && loadedData.courses.length > 0)"
                >
                    <h2 class="text-2xl font-bold text-blog-old-red mb-2">
                        {{ translate('A wide selection of courses') }}
                    </h2>
                    <Button :label="translate('see all', 'capitalize')" icon="pi pi-arrow-right" iconPos="right" 
                        class="p-button-text font-bold text-carrot" 
                        @click="urlHandler.redirectAndScroll('/search')" />
                </div>

                <div class="flex gap-8 border-bottom border-gray-200 mb-8 overflow-x-auto">
                    <button 
                        v-for="tag, index of loadedData.tags"
                        :key="index"
                        :class="loadedData.selectedTag?.id == tag.id ? 'pb-2 font-bold border-b-2 border-carrot text-blog-old-red' : 'pb-2 text-gray hover:text-carrot transition'"
                        @click="selectTag(tag)"
                    >
                        {{ ucfirst(tag.getTranslatedName()) }}
                    </button>
                </div>

                <div class="flex justify-between gap-4" v-if="loadedData.loadingCourses">
                    <Skeleton height="5rem"></Skeleton>
                    <Skeleton height="5rem"></Skeleton>
                    <Skeleton height="5rem"></Skeleton>
                    <Skeleton height="5rem"></Skeleton>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" v-else>
                    <div class="group cursor-pointer" v-for="course, index of loadedData.courses" :key="index" @click.prevent="urlHandler.redirectAndScroll(`/course/${course.uuid}`)">
                        <CoursePhotoComponent :course="course" />
                        <h3 class="font-bold text-secondary leading-tight mb-1">
                            {{ course.title }}
                        </h3>
                        <p class="text-xs text-chain mb-1 text-gray">{{ capitalizeWords(course.authorName) }}</p>
                        <div class="flex items-center gap-1 mb-1">
                            <span class="text-dark-passion-fruit font-bold">{{ course.rating }}</span>
                            <div class="flex text-passion-fruit text-[10px]">
                                <i class="pi pi-star-fill" v-for="index in course.getRating()" :key="index"></i>
                            </div>
                        </div>
                        <p class="font-bold text-lg text-secondary">R${{ course.price }}</p>
                    </div>
                </div>

                <h2 
                    v-if="!loadedData.loadingCourses && loadedData.courses.length == 0" 
                    class="text-2xl font-bold text-blog-old-red mb-2 pb-2 mb-8"
                >
                    {{ translate('No courses found yet. We are producing new content right now!') }}
                </h2>
            </main>

            <div class="w-full">
                <section class="bg-blog-red py-16 text-white text-center overflow-hidden border-t-2 border-b-2 border-blog-gold">
                    <div class="container mx-auto px-4">
                        <h2 class="f-font-times-new-roman italic text-4xl md:text-5xl mb-12 tracking-wide text-white">
                            {{ translate('Pillars') }}
                        </h2>

                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch">
                            <div 
                                v-for="(item, idx) in moreAboutSectionData" 
                                :key="idx"
                                class="bg-[#fff8f1] text-secondary p-8 rounded-[28px] border border-blog-gold shadow-md flex flex-col justify-between transition-transform duration-300 hover:scale-105"
                                :style="{ transform: !isSmallScreen ? item.rotation : 'none' }"
                            >
                                <div class="text-left">
                                    <h3 class="f-font-times-new-roman font-bold text-xl text-blog-old-red mb-4 italic">
                                        {{ translate(item.title) }}
                                    </h3>
                                    <p class="f-font-helvetica text-sm text-gray-700 leading-relaxed font-light">
                                        {{ translate(item.description) }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </section>

        <div class="container mx-auto" v-if="testimoniesData.list && testimoniesData.list.length > 0">
            <section class="px-8 md:px-20 mx-auto py-12">
                <h2 class="text-2xl font-bold text-blog-old-red mt-10 mb-8">
                    {{ translate('What our students are saying') }}
                </h2>

                <section class="pb-12 bg-white relative group">
                    <button @click="scroll('left')"
                        class="absolute left-2 top-1/2 z-10 bg-blog-red text-white w-10 h-10 rounded-full flex items-center justify-center shadow-xl opacity-0 group-hover:opacity-100 transition-opacity disabled:hidden">
                        <i class="pi pi-angle-left"></i>
                    </button>

                    <div ref="scrollContainer"
                        class="flex overflow-x-auto gap-4 px-4 md:px-8 no-scrollbar pb-6 snap-x scroll-smooth select-none cursor-grab active:cursor-grabbing"
                        @mousedown="startDragging" @mouseleave="stopDragging" @mouseup="stopDragging" @mousemove="move">
                        <div v-for="(card, index) in testimoniesData.list" :key="index"
                            class="min-w-[200px] md:min-w-[300px] snap-start">
                            <div
                                class="shadow-lg p-8 bg-blog-beige flex flex-col h-full transition-all shadow-sm rounded-lg">
                                <i class="pi pi-sparkles icon-lg text-blog-old-red opacity-20 mb-4 italic"></i>

                                <p class="text-blog-old-red font-medium leading-relaxed mb-8 h-28 overflow-hidden">
                                    {{ card.content }}
                                </p>

                                <div class="mt-auto flex items-center gap-4 border-t border-cream pt-6">
                                    <img :src="card.avatar || require('@/assets/images/avatar.png')" class="w-12 h-12 rounded-full border border-iron"
                                        :alt="card.author">
                                    <div>
                                        <p class="text-sm font-bold text-blog-old-red">{{ card.author }}</p>
                                        <p class="text-xs text-blog-red">{{ card.role }}</p>
                                    </div>
                                </div>

                                <div
                                    v-if="card.actionText"
                                    class="mt-4 flex items-center gap-1 text-carrot font-bold text-sm cursor-pointer group">
                                    <span>{{ card.actionText }}</span>
                                    <i class="pi pi-angle-right group-hover:translate-x-1 transition-transform"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button @click="scroll('right')"
                        class="absolute right-2 top-1/2 z-10 bg-blog-red text-white w-10 h-10 rounded-full flex items-center justify-center shadow-xl opacity-0 group-hover:opacity-100 transition-opacity">
                        <i class="pi pi-angle-right"></i>
                    </button>
                </section>
            </section>
        </div>

        <section class="px-8 md:px-20 mx-auto py-20 bg-gray-50/50">
            <div class="flex justify-between items-end mb-10">
                <div>
                    <h2 class="text-3xl font-bold text-blog-old-red f-font-times-new-roman italic">
                        {{ translate('Insights & Articles') }}
                    </h2>
                    <p class="text-blog-old-red font-semibold mt-2">
                        {{ translate('Learning tips and news from Teacher Maestra Victoria.') }}
                    </p>
                </div>
                <Button :label="translate('see all', 'capitalize')" icon="pi pi-arrow-right" iconPos="right" 
                    class="p-button-text font-bold text-carrot" 
                    @click="urlHandler.redirectAndScroll('/blog/home')" />
            </div>

            <div v-if="blogData.loading" class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <Skeleton v-for="i in 3" :key="i" height="400px" borderRadius="16px" />
            </div>

            <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <BlogCardComponent v-for="post in blogData.list.slice(0, 3)" :key="post.id" :post="post" 
                    @click="urlHandler.redirectAndScroll(`/blog/detail/${post.id}`)" />
            </div>
        </section>
    </div>

</template>
<script setup>
import { isScreeSmall } from '@/composables/windowSize.js';
import { Button } from 'primevue';
import { onMounted, onUnmounted, reactive, ref } from 'vue';
import { translate } from '@/composables/translate';
import CourseTagService from '@/services/CourseTagService';
import { capitalizeWords, ucfirst } from '@/composables/textUtils';
import CourseService from '@/services/CourseService';
import Skeleton from 'primevue/skeleton';
import urlHandler from '@/composables/urlHandler';
import TestimonyService from '@/services/TestimonyService';
import BlogCardComponent from '@/components/BlogPost/BlogCardComponent.vue';
import BlogPostService from '@/services/BlogPostService';
import CoursePhotoComponent from '@/components/Course/CoursePhotoComponent.vue';

const titleMessage = 'every step counts, even the small ones!';
function getTitleMessageArray() {
    const message = translate(titleMessage);
    
    return message.split('');
}

const moreAboutSectionData = [
    {
        title: 'Communicative approach',
        description: 'Lessons focused on communication (but without neglecting other skills) with relevant everyday topics. Nothing could be closer to learning about life in English, right?'
    },
    {
        title: 'Acceptance and respect',
        description: "Here, respecting the student's pace is essential. We want you to feel comfortable and encouraged to speak without pressure or being told about mistakes or accent – ​​just be kind to your journey, cutie!"
    },
    {
        title: 'Individual monitoring',
        description: 'Feedback and emotional and educational support are part of our DNA, because we know the importance of this support and care throughout a new learning process.'
    },
    {
        title: 'Custom Notion',
        description: 'A personalized, organized, and attractive environment to find all the necessary information about classes, assignments, English in your daily routine, contracts, and everything else you need on this journey.'
    }
];

let showNavigatorsCarousel = ref();
onMounted(() => {
    window.addEventListener('resize', handleResize);

    loadData();
    loadTestimonies();
    loadBlog();
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

function handleResize() {
    showNavigatorsCarousel.value = isScreeSmall(1400);
}

const loadedData = reactive({
    tags: [],
    selectedTag: null,
    courses: [],
    loadingTags: false,
    loadingCourses: false
});
async function loadData() {
    loadedData.loadingTags = true;
    loadedData.loadingCourses = true;

    loadedData.tags = await CourseTagService.all();
    loadedData.selectedTag = loadedData.tags.length > 0 ? loadedData.tags[0] : null;

    loadedData.loadingTags = false;

    await getMostRated(loadedData.selectedTag?.id || null);
}

const testimoniesData = reactive({
    list: [],
    loading: true
});
async function loadTestimonies() {
    testimoniesData.loading = true;

    const data = [];
    const response = await TestimonyService.get();

    for (let testimony of response) {
        data.push({
            author: testimony.title,
            content: testimony.getShortContent(),
            role: testimony.occupation,
            avatar: testimony.getPhoto(),
            actionText: ''
        });
    }

    testimoniesData.list = data;
    testimoniesData.loading = false;
}

const blogData = reactive({
    list: [],
    loading: true
});
async function loadBlog() {
    blogData.loading = true;

    blogData.list = await BlogPostService.get(5);
    blogData.loading = false;
}

async function selectTag(tab = null) {
    loadedData.selectedTag = tab;
    await getMostRated(loadedData.selectedTag?.id || null);
}

async function getMostRated(tagId = null) {
    loadedData.loadingCourses = true;

    loadedData.courses = await CourseService.mostRated(tagId);

    loadedData.loadingCourses = false;
}

const scrollContainer = ref(null);

const scroll = (direction) => {
    const container = scrollContainer.value;
    const scrollAmount = 420;
    if (direction === 'left') {
        container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
};

let isDown = false;
let startX;
let scrollLeft;

const startDragging = (e) => {
    isDown = true;
    startX = e.pageX - scrollContainer.value.offsetLeft;
    scrollLeft = scrollContainer.value.scrollLeft;
};
const stopDragging = () => isDown = false;
const move = (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollContainer.value.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainer.value.scrollLeft = scrollLeft - walk;
};

</script>
<style scoped>

.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>